import dayjs from 'dayjs'
import { createSelector } from 'reselect'
import { namespace } from '.'
import {
  formatDate,
  formatResubmitDate,
  LastDay
} from '../../components/DropdownUpload/helpers'
import { Security } from '../securities/reducer'
import {
  getGridViewState,
  getSecurityOrderDataById
} from '../securities/selectors'
import { StagedOrder } from '../stagedOrders/types'
import { isActiveUserOrder, isTrackedTerminalOrder } from './helpers'
import { State } from './reducer'
import { Order, OrderType } from './types'
import { getTypedArray } from '../securities/helpers'
interface OrderToImport {
  [date: string]: {
    [securityId: number]: {
      sell: {
        price: number
        size: number
        spread: number
        isSpreadOrder: boolean
        allOrNone: boolean
      }
      buy: {
        price: number
        size: number
        spread: number
        isSpreadOrder: boolean
        allOrNone: boolean
      }
    }
  }
}

const byDateDesc = (o1: Order, o2: Order) =>
  o2.submitTime.valueOf() - o1.submitTime.valueOf()

type T = typeof namespace
type HasNsKey = {
  [key in T]: State
}

const getState = <S extends HasNsKey>(state: S): State => state[namespace]

/*
  The canonical collection; every other order view is a projection of it.
  TODO: if selector recomputations become a bottleneck, split into a getActiveUserOrders
  sub-record maintained at the reducer level so the chain doesn't fire on terminal-order changes.
*/
const getCanonicalUserOrders = createSelector(
  [getState],
  (state) => state.userOrders
)

/* Record<id, Order> of orders matching predicate. Synthetic (id '') orders never qualify. */
const createOrdersByIdSelector = (predicate: (order: Order) => boolean) =>
  createSelector([getCanonicalUserOrders], (orders) => {
    const retval: Record<string, Order> = {}
    for (const order of Object.values(orders)) {
      if (order.id !== '' && predicate(order)) {
        retval[order.id] = order
      }
    }
    return retval
  })

export const getUserOrders = createSelector(
  [getCanonicalUserOrders],
  (orders) => Object.values(orders).filter(isActiveUserOrder)
)

export const getOperatorOrders = createSelector([getState], (state) => {
  const retval = []
  for (const o in state.operatorOrders) {
    if (state.operatorOrders.hasOwnProperty(o)) {
      retval.push(state.operatorOrders[o])
    }
  }
  return retval
})

export const getAcceptedOrders = createOrdersByIdSelector(
  (order) => order.status === 'accepted'
)

export const getOperatorOrdersRecord = createSelector(
  [getState],
  (state) => state.operatorOrders
)

export const getCancelledOrders = createOrdersByIdSelector(
  (order) => order.status === 'cancelled' && !!order.initialOrder
)

export const getWaitingForConfirmationOrders = createOrdersByIdSelector(
  (order) => order.status === 'waitingForConfirmation'
)

export const getUserOrdersBySecurityId = createSelector(
  [getUserOrders],
  (orders) => {
    const retval: Record<number, Order[]> = {}
    for (const order of orders) {
      if (!retval.hasOwnProperty(order.securityId)) {
        retval[order.securityId] = []
      }
      retval[order.securityId].push(order)
    }
    return retval
  }
)

export const getFakeTransactionIdMap = createSelector(
  [getState],
  (state) => state.fakeTransactionIdMap
)

export const getUserOrdersByTransactionId = createSelector(
  [getCanonicalUserOrders],
  (orders) => {
    const retval: Record<number, Order> = {}
    for (const order of Object.values(orders)) {
      if (order.transactionId !== undefined) {
        retval[order.transactionId] = order
      }
    }
    return retval
  }
)

export const getAggressErrors = createSelector(
  [getState],
  (state) => (transactionId: number) => state.hitLiftErrors[transactionId]
)

export const getListTradeError = createSelector(
  [getState],
  (state) => (orderId: string) => state.listTradeErrors[orderId]
)

export const getListTradeOrdersForSecurity = createSelector(
  [getState],
  (state) => (securityId: number) => state.listTradeOrders[securityId]
)

export const getTradeConfirmId = createSelector(
  [getState],
  (state) => state.tradeConfirmId
)

/* Active + finished orders that still show in grids/history; excludes synthetic,
   error and cancelled-passive orders (those remain reachable by transaction id). */
export const getAllUserOrders = createSelector(
  [getCanonicalUserOrders],
  (orders) =>
    Object.values(orders).filter(
      (order) => isActiveUserOrder(order) || isTrackedTerminalOrder(order)
    )
)

export const getActivitiesOrders = createSelector(
  [getCanonicalUserOrders],
  (orders) =>
    Object.values(orders).filter(
      (order) =>
        isTrackedTerminalOrder(order) ||
        (order.id !== '' && order.status === 'waitingForConfirmation')
    )
)

export const getOrderById = createSelector(
  [getCanonicalUserOrders],
  (orders) => (orderId: Order['id']) => {
    const order: Order | undefined = orders[orderId]
    return order && (isActiveUserOrder(order) || isTrackedTerminalOrder(order))
      ? order
      : undefined
  }
)

export const getUserOrOperatorOrderById = createSelector(
  [getOrderById, getOperatorOrdersRecord],
  (getOrder, operatorOrders) =>
    (orderId: Order['id']): Order | undefined =>
      getOrder(orderId) ?? operatorOrders[orderId]
)

export const getAggressorOrderByInitialOrder = createSelector(
  [getUserOrders],
  (userOrders) => (initialOrderId: string) =>
    [...userOrders]
      .sort(byDateDesc)
      .find((o) => o.initialOrder?.id === initialOrderId)
)

export const getListTradeOrders = createSelector(
  [getAllUserOrders],
  (orders) => (securityId: number, type: OrderType, listId: number) => {
    // can't use userOrdersBySecurityId, because it doesn't capture finished orders
    const sortedOrders = orders
      .filter(
        (order) =>
          order.securityId === securityId &&
          order.type === type &&
          order.listTradingId === listId &&
          dayjs(order.submitTime).isSame(dayjs(), 'day')
      )
      .sort((oa, ob) => {
        return dayjs(oa.submitTime).isBefore(dayjs(ob.submitTime)) ? 1 : -1
      })
    // ensure we only get the latest order with a hash
    const ordersHash = sortedOrders.reduce((hash, order) => {
      return { ...hash, [order.id]: order }
    }, {} as Record<Order['id'], Order>)
    const orderIds = Object.keys(ordersHash)
    const reverseMultiplier = type === 'sell' ? -1 : 1
    return orderIds
      .map((id) => ordersHash[id])
      .sort((o1, o2) => {
        return (o1.price - o2.price) * reverseMultiplier
      })
  }
)

export const listTradeOrdersBySecurity = createSelector(
  [getAllUserOrders],
  (allUserOrders) => (listId: number, type?: OrderType) => {
    const filteredOrders = allUserOrders.filter(
      (order) =>
        order.listTradingId === listId &&
        dayjs(order.submitTime).isSame(dayjs(), 'day') &&
        (!type || order.type === type)
    )
    const result: Record<number, Order[]> = {}
    return filteredOrders.reduce((acc, order) => {
      const securityOrders = acc[order.securityId] ?? getTypedArray<Order>()
      securityOrders.push(order)
      acc[order.securityId] = securityOrders
      return acc
    }, result)
  }
)

export const getHedgeOrder = createSelector(
  [getAllUserOrders],
  (allOrders) => (orderId: string) =>
    allOrders.find((order) => order.corpOrderId === orderId)
)

export const getOrderByTransactionId = createSelector(
  [getUserOrdersByTransactionId, getFakeTransactionIdMap],
  (userOrdersByTransactionId, fakeTransactionIdMap) =>
    (transactionId: number) => {
      // fall back to the given id when it was never re-keyed from a fake id
      const realTransactionId: number | undefined =
        fakeTransactionIdMap[transactionId]
      const order: Order | undefined =
        userOrdersByTransactionId[realTransactionId ?? transactionId]
      return order
    }
)

export const getInitialOrder = createSelector(
  [getOrderById],
  (getOrder) => (orderId: string) => getOrder(orderId)?.initialOrder
)

export const getOrderBySecurityAndType = createSelector(
  getUserOrdersBySecurityId,
  (orders) => (securityId: number, type: OrderType) => {
    const securityOrders: Order[] | undefined = orders[securityId]
    return (
      securityOrders &&
      [...securityOrders].sort(byDateDesc).find((order) => order.type === type)
    )
  }
)

export const getPendingUserOrderForSecurity = createSelector(
  [getUserOrdersBySecurityId],
  (userOrders) => (securityId: number, orderType: OrderType) => {
    const securityOrders: Order[] | undefined = userOrders[securityId]
    return securityOrders?.find(
      (order) => order.status === 'pending' && order.type === orderType
    )
  }
)

/* Factory: a per-(securityId, orderType) instance selects the order itself, so
   useSelector's reference check skips re-renders on unrelated order batches. */
export const makeGetPendingUserOrderForSecurity = (
  securityId: Security['id'],
  orderType: OrderType
) =>
  createSelector([getUserOrdersBySecurityId], (userOrders) => {
    const securityOrders: Order[] | undefined = userOrders[securityId]
    return securityOrders?.find(
      (order) => order.status === 'pending' && order.type === orderType
    )
  })

export const getAllPendingUserOrders = createSelector(
  [getUserOrdersBySecurityId],
  (userOrders) =>
    Object.values(userOrders)
      .flatMap((orders) => orders)
      .filter((order) => order.status === 'pending')
)

export const getOrderValidations = createSelector(
  [getState],
  (state) => state.validations
)

export type ValidationsType = ReturnType<typeof getOrderValidations>

/* Order errors reach the UI via state.validations only — isActiveUserOrder filters
   status 'error' records out of getUserOrders before these selectors can see them. */
export const getErrorForOrder = createSelector(
  [getOrderValidations],
  (validations) => (securityId: Security['id'], orderType: OrderType) =>
    validations.find(
      (v) => v.securityId === securityId && v.orderType === orderType
    )?.error
)

/* Factory sibling of getErrorForOrder: selects the error value itself for
   per-panel subscriptions instead of a lookup function that churns. */
export const makeGetErrorForOrder = (
  securityId: Security['id'],
  orderType: OrderType
) =>
  createSelector(
    [getOrderValidations],
    (validations) =>
      validations.find(
        (v) => v.securityId === securityId && v.orderType === orderType
      )?.error
  )

export const getValidationResultForOrder = createSelector(
  [getOrderValidations],
  (validations) => (securityId: Security['id'], orderType: OrderType) =>
    validations.find(
      (validation) =>
        validation.securityId === securityId &&
        validation.orderType === orderType
    )
)

/* Factory sibling of getValidationResultForOrder: selects the validation record
   itself for per-panel subscriptions instead of a lookup function that churns. */
export const makeGetValidationResultForOrder = (
  securityId: Security['id'],
  orderType: OrderType
) =>
  createSelector([getOrderValidations], (validations) =>
    validations.find(
      (validation) =>
        validation.securityId === securityId &&
        validation.orderType === orderType
    )
  )

export const getLastErrorDateForDisplayedOrders = createSelector(
  [getGridViewState, getState],
  (getViewState, state) => (gridIndex: number) =>
    [...state.validations]
      .sort((v1, v2) => v2.date.valueOf() - v1.date.valueOf())
      .find((validation) =>
        (getViewState(gridIndex).securityIds ?? []).includes(
          validation.securityId
        )
      )?.date
)
export const getErrorsForDate = createSelector(
  [getState],
  (state) => (gridIndex: number, d: Date | undefined) =>
    d === undefined ? [] : state.validations.filter((v) => v.date === d)
)

export const isMyOrdersOpen = createSelector(
  [getState],
  (state) => (gridIndex: number) => Boolean(state.myOrdersIsOpen[gridIndex])
)

export const getOrdersFetched = createSelector(
  [getState],
  (state) => state.ordersFetched
)

export const getUserOrdersToAddStagedOrdersByDatePicker = createSelector(
  [getUserOrders],
  (userOrders) =>
    [...userOrders]
      .sort((a, b) => a.submitTime.getTime() - b.submitTime.getTime())
      .reduce((acc, currentUserOrder) => {
        const date = currentUserOrder.submitTime.toDateString()
        return {
          ...acc,
          [date]: {
            ...acc[date],
            [currentUserOrder.securityId]: {
              ...acc[date]?.[currentUserOrder.securityId],
              [currentUserOrder.type]: {
                price: currentUserOrder.price,
                size: currentUserOrder.size,
                spread: currentUserOrder.spread,
                isSpreadOrder: currentUserOrder.isSpreadOrder,
                allOrNone: currentUserOrder.allOrNone
              }
            }
          }
        }
      }, {} as OrderToImport)
)

export const getResubmitOrders = createSelector(
  [getState],
  (state) => state.resubmitOrders
)

export const isOrderMine = createSelector(
  [getUserOrders],
  (orders) => (orderId: Order['id']) => orders.some((o) => o.id === orderId)
)

export const isBestOrderMine = createSelector(
  [isOrderMine, getSecurityOrderDataById],
  (orderIsMine, getSecurity) =>
    (securityId: Security['id'], orderType: OrderType) => {
      const security = getSecurity(securityId)
      if (!security) return false
      const order = orderType === 'buy' ? security.bestBid : security.bestOffer
      if (!order) return false
      return orderIsMine(order.id)
    }
)

export const getXLastDayAndOrdersAssociate = createSelector(
  [getResubmitOrders],
  (resubmitOrders) => (numberOfDays: number) => {
    const result: LastDay[] = []
    const date = new Date()
    let i = 0
    while (result.length < numberOfDays) {
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        const currentDate = new Date(date)
        const formattedDate = formatResubmitDate(currentDate)
        const getOrderByDate = resubmitOrders?.[formattedDate] ?? {}
        const stagedOrders: StagedOrder[] = []

        if (getOrderByDate) {
          for (const secId in getOrderByDate) {
            if (getOrderByDate.hasOwnProperty(secId)) {
              const bidAndOffer = getOrderByDate[secId]
              for (const side in bidAndOffer) {
                if (bidAndOffer.hasOwnProperty(side)) {
                  const sideAsOrderType = side as OrderType
                  const ord = bidAndOffer[sideAsOrderType]
                  stagedOrders.push({
                    size: ord.size,
                    price: ord.price,
                    orderType: ord.type,
                    securityId: ord.securityId,
                    spread: ord.spread,
                    isSpreadOrder: ord.isSpreadOrder,
                    allOrNone: ord.allOrNone,
                    individualMin: ord.individualMin,
                    custId: 0,
                    tob: ord.tob
                  })
                }
              }
            }
          }
        }

        result.push({
          id: i,
          dateFormat: formatDate(date),
          stagedOrders
        })
        i++
      }
      date.setDate(date.getDate() - 1)
    }

    return result
  }
)
